export const MEMBERS_BUNDLE_FINGERPRINT = Object.freeze({
  otaVersion: typeof __MEMBERS_OTA_VERSION__ === 'undefined' ? '' : __MEMBERS_OTA_VERSION__,
  gitSha: typeof __MEMBERS_GIT_SHA__ === 'undefined' ? '' : __MEMBERS_GIT_SHA__,
})

if (typeof window !== 'undefined' && !Object.prototype.hasOwnProperty.call(window, '__PRIVANA_MEMBERS_BUILD__')) {
  Object.defineProperty(window, '__PRIVANA_MEMBERS_BUILD__', {
    value: MEMBERS_BUNDLE_FINGERPRINT,
    writable: false,
    configurable: false,
    enumerable: true,
  })
}

export function loadedBundleAssetUrls(documentObject = document) {
  return {
    stylesheets: [...documentObject.querySelectorAll('link[rel="stylesheet"][href]')].map((node) => node.href),
    scripts: [...documentObject.querySelectorAll('script[src]')].map((node) => node.src),
  }
}

export function membersBundleBaseUrl({
  documentObject = document,
  locationObject = window.location,
  basePath = import.meta.env.BASE_URL,
} = {}) {
  const documentUrl = documentObject.baseURI || locationObject.href
  return new URL(basePath || '/', documentUrl)
}

export function membersBundleManifestUrl(options = {}) {
  return new URL('members-bundle-manifest.json', membersBundleBaseUrl(options))
}

function assetPath(url, bundleBaseUrl) {
  const assetUrl = new URL(url, bundleBaseUrl)
  const basePath = bundleBaseUrl.pathname.endsWith('/') ? bundleBaseUrl.pathname : `${bundleBaseUrl.pathname}/`
  if (!assetUrl.pathname.startsWith(basePath)) return null
  return assetUrl.pathname.slice(basePath.length)
}

async function hashResponse(url, fetcher, cryptoObject) {
  const response = await fetcher(url, { cache: 'no-store' })
  if (!response.ok) throw new Error(`asset unavailable (${response.status})`)
  const bytes = await response.arrayBuffer()
  const digest = await cryptoObject.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('')
}

export async function verifyRunningMembersBundle({
  documentObject = document,
  locationObject = window.location,
  fetcher = window.fetch.bind(window),
  cryptoObject = window.crypto,
  fingerprint = MEMBERS_BUNDLE_FINGERPRINT,
  basePath = import.meta.env.BASE_URL,
} = {}) {
  if (!documentObject.getElementById('root')?.dataset?.reactMounted) return { ok: false, reason: 'react-root-not-mounted' }
  if (!fingerprint?.otaVersion || !fingerprint?.gitSha) return { ok: false, reason: 'build-fingerprint-missing' }
  const { stylesheets, scripts } = loadedBundleAssetUrls(documentObject)
  if (!stylesheets.length) return { ok: false, reason: 'primary-stylesheet-missing' }
  const stylesheetNodes = [...documentObject.querySelectorAll('link[rel="stylesheet"][href]')]
  if (stylesheetNodes.some((node) => !node.sheet)) return { ok: false, reason: 'primary-stylesheet-not-loaded' }
  try {
    const bundleBaseUrl = membersBundleBaseUrl({ documentObject, locationObject, basePath })
    const response = await fetcher(membersBundleManifestUrl({ documentObject, locationObject, basePath }), { cache: 'no-store' })
    if (!response.ok) return { ok: false, reason: 'manifest-unavailable' }
    const manifest = await response.json()
    if (manifest.otaVersion !== fingerprint.otaVersion || manifest.gitSha !== fingerprint.gitSha) return { ok: false, reason: 'manifest-fingerprint-mismatch' }
    const expected = new Map(manifest.assets.map((asset) => [asset.filename, asset.sha256]))
    const loaded = [...stylesheets, ...scripts].map((url) => [url, assetPath(url, bundleBaseUrl)])
    if (loaded.some(([, path]) => !expected.has(path))) return { ok: false, reason: 'loaded-asset-not-in-manifest' }
    if (!cryptoObject?.subtle) return { ok: false, reason: 'asset-hash-unavailable' }
    for (const [url, path] of loaded) {
      if (await hashResponse(url, fetcher, cryptoObject) !== expected.get(path)) return { ok: false, reason: 'loaded-asset-hash-mismatch' }
    }
    return { ok: true, manifest, stylesheets, scripts }
  } catch {
    return { ok: false, reason: 'bundle-verification-failed' }
  }
}
